import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { withFirebase } from '../Firebase';
import * as ROLES from '../../constants/roles';
import { ReactComponent as EditIcon } from '../../svg/tree-edit.svg';
import { ReactComponent as DeleteIcon } from '../../svg/tree-delete.svg';
import { ReactComponent as CheckIcon } from '../../svg/tree-check.svg';
import { ReactComponent as CloseIcon } from '../../svg/delete-svgrepo-com.svg';
import { ReactComponent as UserIcon } from '../../svg/tree-org.svg';

import './users.css';
import '../Admin/admin.css';

const ROLE_COLORS = {
  ADMIN: '#ef4444',
  USER: '#3b82f6',
  GUEST: '#f59e0b',
};

const getRoleColor = (role) => ROLE_COLORS[role] || 'var(--pico-muted-color)';

const formatRelativeTime = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const ACTION_COLORS = {
  USER: '#3b82f6',
  PROJECT: '#8b5cf6',
  PAGE: '#f59e0b',
  PLANNING: '#10b981',
  ASSEMBLY: '#ec4899',
  RESOURCE: '#06b6d4',
};

const getActionColor = (action) => {
  const key = Object.keys(ACTION_COLORS).find(k => action?.includes(k));
  return key ? ACTION_COLORS[key] : 'var(--pico-muted-color)';
};

// ─── Expanded detail panel for a single user ─────────────────────────────────
const UserDetailPanel = ({ user, firebase, activityLogs }) => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(null);
  const [rmStats, setRmStats] = useState(null);
  const [messageCount, setMessageCount] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);

  // Activity for this user (from parent's activityLogs)
  const userActivity = useMemo(() =>
    (activityLogs || []).filter(log => log.actorUid === user.uid).slice(0, 8),
    [activityLogs, user.uid]
  );

  const fetchUserData = useCallback(async () => {
    setLoadingDetail(true);
    try {
      // Fetch preferences
      const prefsSnap = await firebase.user(user.uid).child('preferences').once('value');
      setPreferences(prefsSnap.val());

      // Fetch resource management stats
      const rmSnap = await firebase.user(user.uid).child('resourceManagement').once('value');
      const rm = rmSnap.val();
      if (rm) {
        setRmStats({
          projects: rm.projects ? Object.keys(rm.projects).length : 0,
          resources: rm.resources ? Object.keys(rm.resources).length : 0,
          assignments: rm.assignments ? Object.keys(rm.assignments).length : 0,
        });
      } else {
        setRmStats({ projects: 0, resources: 0, assignments: 0 });
      }

      // Count messages by this user
      const msgSnap = await firebase.messages()
        .orderByChild('userId').equalTo(user.uid).once('value');
      const msgs = msgSnap.val();
      setMessageCount(msgs ? Object.keys(msgs).length : 0);
    } catch {
      setPreferences(null);
      setRmStats({ projects: 0, resources: 0, assignments: 0 });
      setMessageCount(0);
    }
    setLoadingDetail(false);
  }, [firebase, user.uid]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  if (loadingDetail) {
    return (
      <div className="user-card-details">
        <div className="user-detail-loading" aria-busy="true">Loading...</div>
      </div>
    );
  }

  const lastProject = preferences?.replan?.selectedProject || preferences?.shapelist?.selectedProject;
  const customScales = preferences?.customScales;
  const lastNotif = preferences?.lastNotificationCheckTime;

  return (
    <div className="user-card-details">
      {/* Quick stats row */}
      <div className="user-stats-row">
        <div className="user-stat-tile">
          <span className="user-stat-value">{userActivity.length}</span>
          <span className="user-stat-label">Events</span>
        </div>
        <div className="user-stat-tile">
          <span className="user-stat-value">{messageCount ?? 0}</span>
          <span className="user-stat-label">Messages</span>
        </div>
        <div className="user-stat-tile">
          <span className="user-stat-value">{rmStats?.projects ?? 0}</span>
          <span className="user-stat-label">RM Projects</span>
        </div>
        <div className="user-stat-tile">
          <span className="user-stat-value">{rmStats?.resources ?? 0}</span>
          <span className="user-stat-label">Resources</span>
        </div>
        <div className="user-stat-tile">
          <span className="user-stat-value">{rmStats?.assignments ?? 0}</span>
          <span className="user-stat-label">Assignments</span>
        </div>
      </div>

      {/* Info grid */}
      <div className="user-info-grid">
        <div className="user-detail-row">
          <span className="user-detail-label">UID</span>
          <code className="user-detail-value">{user.uid}</code>
        </div>
        <div className="user-detail-row">
          <span className="user-detail-label">Email Verified</span>
          <span className={`user-detail-value ${user.emailVerified ? 'detail-ok' : 'detail-warn'}`}>
            {user.emailVerified ? 'Yes' : 'No'}
          </span>
        </div>
        <div className="user-detail-row">
          <span className="user-detail-label">Provider</span>
          <span className="user-detail-value">{user.signInProvider || '—'}</span>
        </div>
        <div className="user-detail-row">
          <span className="user-detail-label">Created</span>
          <span className="user-detail-value">
            {user.createdAt ? new Date(user.createdAt).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
          </span>
        </div>
        {lastProject && (
          <div className="user-detail-row">
            <span className="user-detail-label">Last Project</span>
            <span className="user-detail-value user-detail-project">{lastProject}</span>
          </div>
        )}
        {customScales && customScales.length > 0 && (
          <div className="user-detail-row">
            <span className="user-detail-label">Custom Scales</span>
            <span className="user-detail-value">{customScales.map(s => s.label).join(', ')}</span>
          </div>
        )}
        {lastNotif && (
          <div className="user-detail-row">
            <span className="user-detail-label">Last Seen</span>
            <span className="user-detail-value">{formatRelativeTime(lastNotif)}</span>
          </div>
        )}
      </div>

      {/* Recent activity */}
      {userActivity.length > 0 && (
        <div className="user-activity-section">
          <h6 className="user-section-title">{t('admin.tab_activity')}</h6>
          <div className="user-activity-list">
            {userActivity.map(log => (
              <div key={log.id} className="user-activity-entry">
                <span className="user-activity-dot" style={{ background: getActionColor(log.action) }} />
                <span className="user-activity-action" style={{ color: getActionColor(log.action) }}>
                  {log.action?.replace(/_/g, ' ')}
                </span>
                {log.organization && <span className="user-activity-org">{log.organization}</span>}
                {log.details?.projectName && <span className="user-activity-project">{log.details.projectName}</span>}
                <span className="user-activity-time">{formatRelativeTime(log.timestamp)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const UserList = (props) => {
  const { t } = useTranslation();
  const { firebase, users, loading, activityLogs } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // State for roles editing
  const [editingRoleFor, setEditingRoleFor] = useState(null);
  const [newOrganisation, setNewOrganisation] = useState('');
  const [newRole, setNewRole] = useState(ROLES.USER);
  const [editingOrg, setEditingOrg] = useState(null);
  const [newOrgName, setNewOrgName] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);

  const organizations = useMemo(() => {
    const orgs = new Set();
    (users || []).forEach(user => {
      if (user.roles) {
        Object.keys(user.roles).forEach(org => {
          if (user.roles[org] !== null) orgs.add(org);
        });
      }
    });
    return Array.from(orgs).sort();
  }, [users]);

  const filteredUsers = useMemo(() => {
    let result = users || [];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(user =>
        (user.username && user.username.toLowerCase().includes(q)) ||
        (user.email && user.email.toLowerCase().includes(q)) ||
        (user.uid && user.uid.toLowerCase().includes(q))
      );
    }

    if (orgFilter !== 'all') {
      result = result.filter(user =>
        user.roles && user.roles[orgFilter] !== null && user.roles[orgFilter] !== undefined
      );
    }

    if (roleFilter !== 'all') {
      result = result.filter(user => {
        if (!user.roles) return false;
        return Object.values(user.roles).some(r => r === roleFilter);
      });
    }

    return result;
  }, [users, searchTerm, orgFilter, roleFilter]);

  const onRoleChange = (uid, organisation, event) => {
    const role = event.target.value;
    const roles = {};
    roles[organisation] = role === 'NONE' ? null : role;
    firebase.user(uid).child('roles').update(roles);
  };

  const onAddNewRole = uid => {
    const trimmedOrg = newOrganisation.trim();
    if (!trimmedOrg) return;

    const user = users.find(u => u.uid === uid);
    if (user && user.roles && user.roles[trimmedOrg]) {
      alert(t('users.duplicate_org_alert'));
      return;
    }

    firebase.user(uid).child('roles').update({ [trimmedOrg]: newRole }).then(() => {
      setEditingRoleFor(null);
      setNewOrganisation('');
      setNewRole(ROLES.USER);
    });
  };

  const onRemoveRole = (uid, organisation) => {
    if (window.confirm(t('vcontrol.delete_confirm', { filename: organisation }))) {
      firebase.user(uid).child('roles').child(organisation).remove();
    }
  };

  const onEditOrgName = (uid, oldOrganisation, role) => {
    const trimmedNewOrg = newOrgName.trim();
    if (!trimmedNewOrg || trimmedNewOrg === oldOrganisation) {
      setEditingOrg(null);
      setNewOrgName('');
      return;
    }

    const user = users.find(u => u.uid === uid);
    if (user && user.roles && user.roles[trimmedNewOrg]) {
      alert(t('users.duplicate_rename_alert'));
      return;
    }

    const updates = {
      [oldOrganisation]: null,
      [trimmedNewOrg]: role,
    };

    firebase.user(uid).child('roles').update(updates).then(() => {
      setEditingOrg(null);
      setNewOrgName('');
    });
  };

  return (
    <div className="activity-tab-content">
      {/* Filters bar — same layout as Activity tab */}
      <div className="activity-filters">
        <input
          type="search"
          placeholder={t('admin.search_placeholder')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="activity-search"
        />
        <select value={orgFilter} onChange={e => setOrgFilter(e.target.value)} className="activity-filter-select">
          <option value="all">{t('vcontrol.organisation')} (All)</option>
          {organizations.map(org => (
            <option key={org} value={org}>{org}</option>
          ))}
        </select>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="activity-filter-select">
          <option value="all">All Roles</option>
          {Object.values(ROLES).map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <span className="activity-count">{filteredUsers.length} {t('admin.users')}</span>
      </div>

      {/* User cards */}
      {loading ? (
        <div aria-busy="true">{t('users.loading')}</div>
      ) : filteredUsers.length === 0 ? (
        <article className="glass empty-activity">
          <p>{t('messages.no_messages')}</p>
        </article>
      ) : (
        <div className="activity-timeline">
          {filteredUsers.map(user => {
            const userRoles = user.roles && typeof user.roles === 'object'
              ? Object.entries(user.roles).filter(([, val]) => val !== null)
              : [];
            const isAddingRole = editingRoleFor === user.uid;
            const primaryRole = userRoles.length > 0 ? userRoles[0][1] : null;

            return (
              <div key={user.uid} className="timeline-entry">
                <div className="timeline-line">
                  <span
                    className="timeline-dot"
                    style={{ background: primaryRole ? getRoleColor(primaryRole) : 'var(--pico-muted-color)' }}
                  />
                </div>
                <div className="timeline-card user-card">
                  {/* Header row */}
                  <div className="timeline-card-header">
                    <div className="user-card-identity">
                      <div className="user-card-avatar">
                        <UserIcon className="user-card-avatar-icon" />
                      </div>
                      <div>
                        <span className="timeline-action">{user.username || 'Unnamed'}</span>
                        <span className="user-card-email">{user.email}</span>
                      </div>
                    </div>
                    <div className="user-card-header-actions">
                      <span className="timeline-time">ID: {user.uid.slice(0, 8)}</span>
                      <button
                        className={`user-card-edit-link ${expandedUser === user.uid ? 'active' : ''}`}
                        onClick={() => setExpandedUser(expandedUser === user.uid ? null : user.uid)}
                        title="Toggle details"
                      >
                        <EditIcon className="user-card-edit-icon" />
                      </button>
                    </div>
                  </div>

                  {/* Roles section */}
                  <div className="user-card-roles">
                    {userRoles.length === 0 && (
                      <span className="no-roles-text">{t('users.none_option')}</span>
                    )}
                    {userRoles.map(([org, role]) => {
                      const isEditingThisOrg = editingOrg && editingOrg.uid === user.uid && editingOrg.organisation === org;

                      return (
                        <div key={org} className="user-role-chip">
                          <span className="user-role-dot" style={{ background: getRoleColor(role) }} />
                          {isEditingThisOrg ? (
                            <input
                              type="text"
                              className="user-role-org-input"
                              value={newOrgName}
                              onChange={e => setNewOrgName(e.target.value)}
                              onBlur={() => onEditOrgName(user.uid, org, role)}
                              onKeyPress={e => e.key === 'Enter' && onEditOrgName(user.uid, org, role)}
                              autoFocus
                            />
                          ) : (
                            <span
                              className="user-role-org"
                              onClick={() => { setEditingOrg({ uid: user.uid, organisation: org }); setNewOrgName(org); }}
                              title="Click to rename organization"
                            >
                              {org}
                            </span>
                          )}
                          <select
                            className="user-role-select"
                            value={role}
                            onChange={e => onRoleChange(user.uid, org, e)}
                          >
                            {Object.values(ROLES).map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                            <option value="NONE">{t('users.none_option')}</option>
                          </select>
                          <button className="user-role-remove" onClick={() => onRemoveRole(user.uid, org)}>
                            <DeleteIcon />
                          </button>
                        </div>
                      );
                    })}

                    {isAddingRole ? (
                      <div className="user-role-add-row">
                        <input
                          type="text"
                          className="user-role-org-input"
                          placeholder={t('users.new_organisation_placeholder')}
                          value={newOrganisation}
                          onChange={e => setNewOrganisation(e.target.value)}
                          autoFocus
                        />
                        <select className="user-role-select" value={newRole} onChange={e => setNewRole(e.target.value)}>
                          {Object.values(ROLES).map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <button className="user-role-confirm" onClick={() => onAddNewRole(user.uid)}>
                          <CheckIcon />
                        </button>
                        <button className="user-role-cancel" onClick={() => setEditingRoleFor(null)}>
                          <CloseIcon />
                        </button>
                      </div>
                    ) : (
                      <button className="user-add-org-btn" onClick={() => setEditingRoleFor(user.uid)}>
                        + {t('users.new_organisation_placeholder')}
                      </button>
                    )}
                  </div>

                  {/* Expandable detail panel */}
                  {expandedUser === user.uid && (
                    <UserDetailPanel
                      user={user}
                      firebase={firebase}
                      activityLogs={activityLogs}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default withFirebase(UserList);
